import chunk from 'lodash/chunk';

import { useApiClient } from '@eluve/api-client-provider';
import {
  type PatientImportEditableField,
  type PatientImportResolveKind,
} from '@eluve/api-contract';
import { useToast } from '@eluve/components';

import { errMsg } from '../utils';

type FinalizeResult =
  | { ok: true }
  | { ok: false; blockingCount: number; message: string };

export const useImportRowMutations = (
  tenantId: string,
  jobId: string,
  onMutationSuccess?: () => void | Promise<void>,
) => {
  const apiClient = useApiClient();
  const toast = useToast();

  const afterSuccess = async () => {
    try {
      await onMutationSuccess?.();
    } catch {
      // swallow — parent screen surfaces refetch errors
    }
  };

  const handle = async <T>(
    errorPrefix: string,
    work: () => Promise<T>,
  ): Promise<T> => {
    try {
      const result = await work();
      await afterSuccess();
      return result;
    } catch (err) {
      toast.error(`${errorPrefix}: ${errMsg(err)}`);
      throw err;
    }
  };

  const updateField = (
    rowId: string,
    field: PatientImportEditableField,
    value: string | null,
  ) =>
    handle('Failed to update field', async () => {
      const res = await apiClient.patientImport.updateImportRow({
        params: { tenantId, jobId, rowId },
        body: { field, value },
      });
      if (res.status !== 200) {
        throw new Error(`updateImportRow failed (${res.status})`);
      }
      return res.body;
    });

  const resolveRow = (rowId: string, kind: PatientImportResolveKind) =>
    handle('Failed to resolve row', async () => {
      const res = await apiClient.patientImport.resolveImportRow({
        params: { tenantId, jobId, rowId },
        body: { kind },
      });
      if (res.status !== 200) {
        throw new Error(`resolveImportRow failed (${res.status})`);
      }
      return res.body;
    });

  const unresolveRow = (rowId: string) =>
    handle('Failed to undo resolution', async () => {
      const res = await apiClient.patientImport.unresolveImportRow({
        params: { tenantId, jobId, rowId },
        body: {},
      });
      if (res.status !== 200) {
        throw new Error(`unresolveImportRow failed (${res.status})`);
      }
      return res.body;
    });

  // Backend caps resolveImportRowsBulk at 500 ids per call.
  const RESOLVE_BULK_CHUNK_SIZE = 500;
  const resolveBulk = async (
    rowIds: string[],
    kind: PatientImportResolveKind,
  ) => {
    if (rowIds.length === 0) return { updatedCount: 0 };
    return handle('Failed to bulk-resolve rows', async () => {
      let updatedCount = 0;
      for (const batch of chunk(rowIds, RESOLVE_BULK_CHUNK_SIZE)) {
        const res = await apiClient.patientImport.resolveImportRowsBulk({
          params: { tenantId, jobId },
          body: { rowIds: batch, kind },
        });
        if (res.status !== 200) {
          throw new Error(`resolveImportRowsBulk failed (${res.status})`);
        }
        updatedCount += res.body.updatedCount;
      }
      return { updatedCount };
    });
  };

  // No bulk endpoint: fan out single calls inside one handle() so the parent
  // refetches once, not per row.
  const unresolveBulk = async (rowIds: string[]) => {
    if (rowIds.length === 0) return;
    return handle('Failed to undo resolutions', async () => {
      await Promise.all(
        rowIds.map(async (rowId) => {
          const res = await apiClient.patientImport.unresolveImportRow({
            params: { tenantId, jobId, rowId },
            body: {},
          });
          if (res.status !== 200) {
            throw new Error(`unresolveImportRow failed (${res.status})`);
          }
        }),
      );
    });
  };

  const finalizeJob = async (): Promise<FinalizeResult> => {
    try {
      const res = await apiClient.patientImport.finalizeImport({
        params: { tenantId, jobId },
        body: {},
      });
      if (res.status === 200) return { ok: true };
      if (res.status === 409) {
        return {
          ok: false,
          blockingCount: res.body.blockingIssueCount,
          message: res.body.message,
        };
      }
      throw new Error(
        `finalizeImport failed (${(res as { status: number }).status})`,
      );
    } catch (err) {
      toast.error(`Failed to finalize import: ${errMsg(err)}`);
      throw err;
    }
  };

  return {
    updateField,
    resolveRow,
    unresolveRow,
    resolveBulk,
    unresolveBulk,
    finalizeJob,
  };
};
